import type { ToolCardRef } from '../components/GenerationInfoBlock'

/**
 * Presentation API — generation/estimate (виджет + runners) + библиотека
 * (items/folders).
 *
 * Серверные эндпоинты в backend/app/api/v1/presentations.py.
 *
 * download_url у items — свежий presigned URL (24h TTL); на каждом GET бэк
 * регенерирует его заново. Хранить и переиспользовать через минуты — можно;
 * через часы — нельзя, лучше перезапросить /my.
 */
import api from './client'
import type { GammaImageSource } from '../pages/generate/hooks/useGenerateSettings'

// ── Generation / estimate (widget + старая BotsSlides-форма) ──────

export interface PresentationTemplate {
	id: string
	label: string
}

export interface PresentationEstimatePayload {
	mode: 'edit' | 'create'
	prompt: string
	file_url?: string
	slide_count?: number
	image_count: number
}

export interface PresentationEstimateResponse {
	credits_price: number
	slides_count?: number | null
	estimated_tokens_input: number
	estimated_tokens_output: number
	estimated_cost_usd: number
}

export interface PresentationGeneratePayload {
	/** Папка, в которую ляжет готовая презентация (студийная модель). */
	folder_id?: string
	model: string
	mode: 'edit' | 'create'
	prompt: string
	file_url?: string
	template?: string
	custom_template_url?: string
	slide_count?: number
	credits_price_estimate?: number
	image_urls?: string[]
	image_filenames?: string[]
}

export interface PresentationGenerateResponse {
	ok: boolean
	request_id?: string
	credits_spent?: number
	status?: string
	error?: string
}

/** GET /api/v1/presentations/templates */
export async function listPresentationTemplates(): Promise<PresentationTemplate[]> {
	const res = await api.request<{ ok: boolean; templates: PresentationTemplate[] }>(
		'/api/v1/presentations/templates',
		{ method: 'GET' },
	)
	return res.ok && res.templates ? res.templates : []
}

/** POST /api/v1/presentations/estimate */
export async function estimatePresentation(
	payload: PresentationEstimatePayload,
	signal?: AbortSignal,
): Promise<PresentationEstimateResponse> {
	return api.request<PresentationEstimateResponse>('/api/v1/presentations/estimate', {
		method: 'POST',
		body: payload,
		signal,
	})
}

/** POST /api/v1/presentations/generate-async */
export async function generatePresentation(
	payload: PresentationGeneratePayload,
): Promise<PresentationGenerateResponse> {
	return api.request<PresentationGenerateResponse>(
		'/api/v1/presentations/generate-async',
		{ method: 'POST', body: payload },
	)
}

// ── Gamma API ──────────────────────────────────────────────────────

export interface GammaGeneratePayload {
	prompt: string
	num_cards?: number
	language?: string
	tone?: string
	audience?: string
	image_source?: GammaImageSource
	dimensions?: '16x9' | '4x3' | '1x1' | '4x5' | '9x16'
	theme_id?: string | null
	additional_instructions?: string
	text_amount?: 'brief' | 'medium' | 'detailed' | 'extensive'
	export_as?: 'pptx' | 'pdf' | 'png'
	image_model?: string
	image_style?: string
	text_mode?: 'generate' | 'condense' | 'preserve'
	/** Карта 6 слотов колонтитулов: {pos: {type: text|number|logo, value?}}. */
	footer_slots?: Record<string, { type: string; value?: string }>
	footer_logo_key?: string
	footer_logo_size?: string
	footer_hide_first?: boolean
	footer_hide_last?: boolean
	folder_id?: string
	credits_price_estimate?: number
}

export interface GammaTheme {
	id?: string
	themeId?: string
	name?: string
	/** Цветовые ключи из Gamma — массив строк ('Green', 'dark', 'warm', ...).
	 *  Используется для визуального представления карточки темы на фронте. */
	colorKeywords?: string[]
	[k: string]: unknown
}

/** POST /api/v1/presentations/gamma/generate-async */
export async function generateGammaPresentation(
	payload: GammaGeneratePayload,
): Promise<PresentationGenerateResponse> {
	return api.request<PresentationGenerateResponse>(
		'/api/v1/presentations/gamma/generate-async',
		{ method: 'POST', body: payload },
	)
}

export interface GammaEditPayload {
	item_id: string
	prompt: string
	folder_id?: string
}

/** POST /api/v1/presentations/gamma/edit-async — правки существующей
 *  Gamma-презентации промптом (from-template): структура сохраняется,
 *  меняется только то, о чём просит промпт. Результат — новая запись. */
export async function editGammaPresentation(
	payload: GammaEditPayload,
): Promise<PresentationGenerateResponse> {
	return api.request<PresentationGenerateResponse>(
		'/api/v1/presentations/gamma/edit-async',
		{ method: 'POST', body: payload },
	)
}

/** GET /api/v1/presentations/gamma/themes — кеш на бэке 1 час. */
export async function listGammaThemes(): Promise<GammaTheme[]> {
	try {
		const res = await api.request<{ ok: boolean; themes: GammaTheme[] }>(
			'/api/v1/presentations/gamma/themes',
			{ method: 'GET' },
		)
		return res.themes || []
	} catch {
		return []
	}
}

// ── Library: items + folders ───────────────────────────────────────

export interface SlidePreview {
	slide_number: number
	title: string
	body: string[]
}

export interface SlidesPreview {
	slide_count: number
	dimensions: { width?: number; height?: number }
	slides: SlidePreview[]
}

export interface PresentationItem {
	id: string
	request_id: string | null
	mode: 'edit' | 'create'
	title: string
	slide_count: number
	filename: string
	/** Свежий presigned URL (24h). Для скачивания/предпросмотра. null если MinIO недоступен. */
	download_url: string | null
	slides_preview: SlidesPreview | null
	/** URL hosted-просмотра презентации с оригинальными стилями.
	 *  Только для Gamma-генераций — приходит из их API (params.gamma_url).
	 *  Если есть — рендерим iframe вместо упрощённого текстового slides_preview.
	 *  Для Umnik/Claude и старых записей — null. */
	gamma_url: string | null
	/** Presigned URL на PNG-превью первого слайда (рендерится через
	 *  LibreOffice headless после завершения генерации презентации).
	 *  Используется в карточках на /bots/slides как обложка вместо
	 *  text-fallback. null если: thumbnail ещё не сгенерирован, LibreOffice
	 *  упал на этом PPTX, или запись создана до внедрения этой фичи (для
	 *  старых есть backfill-script на бэке). */
	thumbnail_url: string | null
	folder_ids: string[]
	created_at: string | null
	added_at?: string | null
	// ── Метаданные генерации (LEFT JOIN requests) — модалка превью показывает
	// промпт так же, как «Личный кабинет → Списания». ──
	/** Промпт презентации. У генераций из карточек-инструментов («Тренды»)
	 *  бэк присылает null: их промпт наружу не отдаётся. */
	prompt?: string | null
	/** Промпт скрыт: презентация сгенерирована из карточки-инструмента. */
	prompt_hidden?: boolean
	/** Что юзер дописал сам поверх промпта инструмента. */
	user_prompt?: string | null
	/** Карточка-источник (при prompt_hidden). */
	tool_card?: ToolCardRef | null
	/** Backend-id модели генерации. */
	model?: string | null
	/** Списано кредитов. */
	credits_spent?: number | null
	/** Оценка 👍/👎 (requests.user_rating): 1 / -1 / null. */
	user_rating?: number | null
	/** Правки промптом доступны: Gamma-дека с gamma_id (фаза 0, 31.08). */
	can_edit?: boolean
}

export interface PresentationFolder {
	id: string
	name: string
	cover_url: string | null
	cover_is_manual: boolean
	/** Превью последней деки папки (presigned PNG первого слайда) — лицо
	 *  папки в рельсе, когда ручной обложки нет. Нет у старого бэка. */
	preview_url?: string | null
	item_count: number
	created_at: string | null
	updated_at: string | null
}

export interface MyPresentationsResponse {
	ok: boolean
	items: PresentationItem[]
	total: number
}

export interface FoldersResponse {
	ok: boolean
	folders: PresentationFolder[]
	total: number
}

export interface FolderResponse {
	ok: boolean
	folder: PresentationFolder
	error?: string
}

export interface FolderWithItemsResponse {
	ok: boolean
	folder: PresentationFolder
	items: PresentationItem[]
	error?: string
}

// ── Items ───────────────────────────────────────────────────────────

/** GET /api/v1/presentations/my — список всех презентаций юзера. */
export async function getMyPresentations(): Promise<MyPresentationsResponse> {
	try {
		return await api.request<MyPresentationsResponse>('/api/v1/presentations/my', { method: 'GET' })
	} catch (err) {
		// Graceful fallback пока бэк не задеплоил эндпоинт.
		const msg = err instanceof Error ? err.message.toLowerCase() : ''
		if (msg.includes('404') || msg.includes('not found') || msg.includes('request failed')) {
			return { ok: true, items: [], total: 0 }
		}
		throw err
	}
}

/** PATCH /api/v1/presentations/items/:id — переименовать. */
export async function renamePresentation(
	itemId: string,
	title: string,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/presentations/items/${itemId}`, {
		method: 'PATCH',
		body: { title },
	})
}

/** DELETE /api/v1/presentations/items/:id — удалить из библиотеки. */
export async function deletePresentation(
	itemId: string,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/presentations/items/${itemId}`, { method: 'DELETE' })
}

// ── Folders ─────────────────────────────────────────────────────────

/** GET /api/v1/presentations/folders — список папок. */
export async function getFolders(): Promise<FoldersResponse> {
	try {
		return await api.request<FoldersResponse>('/api/v1/presentations/folders', { method: 'GET' })
	} catch (err) {
		const msg = err instanceof Error ? err.message.toLowerCase() : ''
		if (msg.includes('404') || msg.includes('not found') || msg.includes('request failed')) {
			return { ok: true, folders: [], total: 0 }
		}
		throw err
	}
}

/** POST /api/v1/presentations/folders */
export async function createFolder(params: {
	name: string
	cover_url?: string | null
}): Promise<FolderResponse> {
	return api.request<FolderResponse>('/api/v1/presentations/folders', {
		method: 'POST',
		body: { name: params.name, cover_url: params.cover_url ?? null },
	})
}

/** GET /api/v1/presentations/folders/:id — папка + items. */
export async function getFolder(folderId: string): Promise<FolderWithItemsResponse> {
	return api.request<FolderWithItemsResponse>(`/api/v1/presentations/folders/${folderId}`, { method: 'GET' })
}

/** PATCH /api/v1/presentations/folders/:id — rename / cover. */
export async function updateFolder(
	folderId: string,
	params: { name?: string; cover_url?: string | null; cover_is_manual?: boolean },
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/presentations/folders/${folderId}`, { method: 'PATCH', body: params })
}

/** DELETE /api/v1/presentations/folders/:id — удалить папку (items остаются). */
export async function deleteFolder(
	folderId: string,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/presentations/folders/${folderId}`, { method: 'DELETE' })
}

/** POST /api/v1/presentations/folders/:id/items — добавить пачкой. */
export async function addItemsToFolder(
	folderId: string,
	itemIds: string[],
): Promise<{ ok: boolean; inserted?: number; error?: string }> {
	return api.request(`/api/v1/presentations/folders/${folderId}/items`, {
		method: 'POST',
		body: { item_ids: itemIds },
	})
}

/** DELETE /api/v1/presentations/folders/:id/items/:item_id */
export async function removeItemFromFolder(
	folderId: string,
	itemId: string,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/presentations/folders/${folderId}/items/${itemId}`, {
		method: 'DELETE',
	})
}
